/**
 * Pure symbol-completion projection logic (WP7 spec: "Projection/derivation logic lives in pure,
 * unit-tested lib functions").
 *
 * `symbol_level_cost` (see V3 migration / `docs/specs/WP7-goals.md` "Read first") stores a
 * per-`symbolType` **cumulative total to max level** at the sentinel `level = 0` row — the research
 * (doc 03 §5) never gives a per-level breakdown, so today every seeded type is totals-only. This
 * module still supports a real per-level curve if one is ever added (rows with `level > 0`), but it
 * NEVER fabricates one: `hasPerLevelCost` is the single gate that decides which of the two response
 * shapes `projectSymbolCompletion` returns, and the totals-only branch never invents an ETA.
 */
import type { SymbolLevelCostDto } from './reference';

/**
 * True only when at least one real per-level row (`level > 0`) exists for this symbol type.
 * `costs` is expected to already be filtered/joined to one symbol type by the caller.
 */
export function hasPerLevelCost(costs: readonly SymbolLevelCostDto[]): boolean {
  return costs.some((c) => c.level > 0);
}

export interface SymbolProjectionInput {
  level: number;
  maxLevel: number;
  /** Symbols currently banked toward the next level-up. */
  symbolsHeld: number;
  /** Symbols gained per day from the character's own daily source, if known. */
  dailyRate: number | null;
  /** Every `symbol_level_cost` row for this symbol's `type` (both the sentinel and any per-level rows). */
  levelCosts: readonly SymbolLevelCostDto[];
  /** The sentinel (`level = 0`) row's total-to-max, when no per-level curve exists. */
  totalToMax: SymbolLevelCostDto | null;
}

export type SymbolProjection =
  | {
      /** A real per-level curve exists: an ETA can be computed without fabricating anything. */
      available: true;
      symbolsRemaining: number;
      daysRemaining: number | null;
    }
  | {
      /** Totals-only data: progress can be shown, but no ETA — doing so would require inventing a
       *  per-level breakdown the research never gave (WP7 spec: "never fabricate per-level costs"). */
      available: false;
      totalSymbolsToMax: number | null;
      totalMesoToMax: number | null;
    };

export function projectSymbolCompletion(input: SymbolProjectionInput): SymbolProjection {
  const perLevelRows = input.levelCosts.filter((c) => c.level > 0).sort((a, b) => a.level - b.level);

  if (perLevelRows.length > 0) {
    // Sum the symbols required for every level strictly above the character's current one, then
    // subtract what's already banked toward the very next level.
    let symbolsRemaining = 0;
    for (const row of perLevelRows) {
      if (row.level > input.level && row.level <= input.maxLevel) {
        symbolsRemaining += row.symbolsRequired;
      }
    }
    symbolsRemaining = Math.max(0, symbolsRemaining - input.symbolsHeld);
    const daysRemaining =
      input.dailyRate && input.dailyRate > 0 ? Math.ceil(symbolsRemaining / input.dailyRate) : null;
    return { available: true, symbolsRemaining, daysRemaining };
  }

  return {
    available: false,
    totalSymbolsToMax: input.totalToMax?.symbolsRequired ?? null,
    totalMesoToMax: input.totalToMax?.mesoCost ?? null,
  };
}

/** `level / maxLevel` as a 0-1 fraction, for a progress bar — always computable regardless of cost data. */
export function symbolProgressFraction(level: number, maxLevel: number): number {
  if (maxLevel <= 0) {
    return 0;
  }
  return Math.min(1, Math.max(0, level / maxLevel));
}
